/**
 * Ролик секции: стартует, когда блок доскроллили до экрана.
 *
 * Перенесён из template (videoInView.js) и дополнен под /cartilox-t/:
 *   • «Многокомпонентный состав» — таблетка раскрывается и остаётся в конечном
 *     кадре (без loop); при каждом возвращении в блок — заново (watchReplay);
 *   • «Нутриентная поддержка» — волокно бесконечно (loop); за кадром — пауза,
 *     чтобы зацикленный ролик не держал декодер и батарею.
 *
 * Разметка:
 *   <div class="js-video-in-view" data-lazy-function="videoInView">
 *       <video data-video-in-view muted playsinline preload="none" poster="…"
 *              [loop] [data-reveal-at="2.2"] [data-src-mob="…-mob.mp4"]>
 *           <source src="…" type="video/mp4">
 *       </video>
 *   </div>
 *
 * Загрузка начинается за экран до блока (watchApproach), одноразовый ролик
 * стартует только скачанным целиком (whenReady) — иначе на мобильной сети он
 * замирал посреди анимации.
 *
 * Запуск — отдельный экземпляр vanilla-lazyload (lazyLoading.js, класс
 * `js-video-in-view`, порог −150: ролик начинается, когда блок уже на экране,
 * а не за 300px до него, как у общего `.js-script`).
 *
 * `data-reveal-at` (секунды) — с этого момента ближайший `[data-video-host]`
 * получает `is-video-revealed`: так на таблетке появляются круг «9» и подпись,
 * которые в первых кадрах легли бы поверх ещё целой капсулы. Если ролик не
 * сыграет (отказ автоплея, prefers-reduced-motion), ролик ставится на
 * последний кадр и подписи открываются сразу — блок выглядит как статичный
 * макет.
 *
 * Автоплей без звука на iOS — только с `muted` и `playsinline`. play() может
 * быть отклонён (режим энергосбережения, настройка сайта): тогда повтор через
 * 1.2 с и на первое касание/клик, если ролик в кадре (подробно — template,
 * docs/video.md).
 */
itsoftApp.createModule("videoInView", function () {
    const module = this;

    const PLAYED_CLASS = "is-played";
    const REVEALED_CLASS = "is-video-revealed";

    const hostOf = function (video) {
        return video.closest("[data-video-host]");
    };

    const setRevealed = function (video, state) {
        const host = hostOf(video);

        if (host) {
            host.classList.toggle(REVEALED_CLASS, state);
        }
    };

    /* Подписи по времени ролика */
    const watchReveal = function (video) {
        const at = parseFloat(video.getAttribute("data-reveal-at"));

        if (!(at >= 0) || video.dataset.revealWatched) {
            return;
        }
        video.dataset.revealWatched = "1";

        const check = function () {
            setRevealed(video, video.currentTime >= at || video.ended);
        };

        video.addEventListener("timeupdate", check);
        video.addEventListener("ended", check);
        video.addEventListener("seeked", check);
    };

    /* Конечное состояние без воспроизведения: последний кадр + подписи */
    const showFinalState = function (video) {
        setRevealed(video, true);

        if (video.loop) {
            return;
        }

        const seekToEnd = function () {
            if (video.duration && isFinite(video.duration)) {
                video.currentTime = Math.max(video.duration - 0.05, 0);
            }
        };

        if (video.readyState >= 1) {
            seekToEnd();
        } else {
            video.addEventListener("loadedmetadata", seekToEnd, { once: true });
        }
    };

    const watchVisibility = function (video) {
        if (!window.IntersectionObserver || video.dataset.visibilityWatched) {
            return;
        }
        video.dataset.visibilityWatched = "1";

        const observer = new IntersectionObserver(function (entries) {
            for (let i = 0; i < entries.length; i += 1) {
                if (entries[i].isIntersecting) {
                    const resumed = video.play();

                    if (resumed && typeof resumed.catch === "function") {
                        resumed.catch(function () {});
                    }
                } else if (!video.paused) {
                    video.pause();
                }
            }
        }, { rootMargin: "10% 0px" });

        observer.observe(video);
    };

    const reducedMotion = function () {
        return window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    };

    /* Мобильная версия файла (`data-src-mob`) — ниже планшета. Выбираем до
       load(): `media` у <source> поддержан не везде */
    const MOB_QUERY = "(max-width: 767px)";

    const chooseSource = function (video) {
        const mob = video.getAttribute("data-src-mob");
        const source = video.querySelector("source");

        if (mob && source && window.matchMedia && window.matchMedia(MOB_QUERY).matches) {
            source.src = mob;
        }
    };

    /* Начать загрузку (один раз): preload="none" в разметке — до подхода к
       блоку трафик не тратим */
    const prime = function (video) {
        if (video.dataset.primed) {
            return;
        }
        video.dataset.primed = "1";
        chooseSource(video);
        video.preload = "auto";
        video.load();
    };

    /* Загрузка заранее — примерно за экран до блока. Ролик в 4 с при 6 Мбит/с
       на мобильной сети начинал играть недогруженным и замирал посреди
       анимации (замер 23.10.2026: на 4 Мбит/с — 2 остановки, 7.1 с вместо 4) */
    const watchApproach = function () {
        const videos = document.querySelectorAll("[data-video-in-view]");

        if (!videos.length) {
            return;
        }

        if (!window.IntersectionObserver) {
            return;
        }

        const observer = new IntersectionObserver(function (entries) {
            for (let i = 0; i < entries.length; i += 1) {
                if (entries[i].isIntersecting) {
                    observer.unobserve(entries[i].target);

                    if (!reducedMotion()) {
                        prime(entries[i].target);
                    }
                }
            }
        }, { rootMargin: "100% 0px" });

        for (let i = 0; i < videos.length; i += 1) {
            observer.observe(videos[i]);
        }
    };

    /* Одноразовый ролик стартует, когда скачан ЦЕЛИКОМ: короткую анимацию
       нельзя останавливать на догрузку. Не дождались за READY_TIMEOUT —
       играем как есть (лучше рывок, чем вечный постер) */
    const READY_TIMEOUT = 8000;

    const fullyBuffered = function (video) {
        const b = video.buffered;

        return video.readyState >= 4 && b.length > 0 && video.duration > 0 &&
            b.end(b.length - 1) >= video.duration - 0.1;
    };

    const whenReady = function (video, fn) {
        if (video.loop || fullyBuffered(video)) {
            fn();
            return;
        }

        let done = false;
        const finish = function () {
            if (done) {
                return;
            }
            done = true;
            video.removeEventListener("progress", check);
            video.removeEventListener("canplaythrough", check);
            window.clearTimeout(timer);
            fn();
        };
        const check = function () {
            if (fullyBuffered(video)) {
                finish();
            }
        };
        const timer = window.setTimeout(finish, READY_TIMEOUT);

        video.addEventListener("progress", check);
        video.addEventListener("canplaythrough", check);
    };

    const pending = [];
    let gestureBound = false;

    const inViewport = function (el) {
        const r = el.getBoundingClientRect();

        return r.bottom > 0 && r.top < (window.innerHeight || 0);
    };

    const tryPending = function () {
        for (let i = pending.length - 1; i >= 0; i -= 1) {
            const video = pending[i];

            if (!inViewport(video)) {
                continue;
            }

            /* Одноразовый ролик после отказа стоит на последнем кадре —
               запускаем его сначала, подписи снова ждут своей секунды */
            if (!video.loop) {
                video.currentTime = 0;
                setRevealed(video, false);
            }

            const resumed = video.play();

            if (resumed && typeof resumed.then === "function") {
                resumed.then(function () {
                    const idx = pending.indexOf(video);

                    if (idx !== -1) {
                        pending.splice(idx, 1);
                    }
                }).catch(function () {
                    showFinalState(video);
                });
            }
        }

        if (!pending.length && gestureBound) {
            document.removeEventListener("touchstart", tryPending);
            document.removeEventListener("click", tryPending);
            gestureBound = false;
        }
    };

    const retryLater = function (video) {
        if (pending.indexOf(video) === -1) {
            pending.push(video);
        }
        window.setTimeout(tryPending, 1200);

        if (!gestureBound) {
            document.addEventListener("touchstart", tryPending, { passive: true });
            document.addEventListener("click", tryPending);
            gestureBound = true;
        }
    };

    const start = function (video) {
        whenReady(video, function () {
            const started = video.play();

            if (started && typeof started.catch === "function") {
                started.catch(function (err) {
                    DEBUG && console.log("videoInView: автоплей отклонён — " + err.name);
                    showFinalState(video);
                    retryLater(video);
                });
            }
        });
    };

    /**
     * Одноразовый ролик — заново при каждом возвращении в блок (менеджер,
     * 23.10.2026). Ушёл из кадра целиком — пауза и перемотка в начало
     * (подписи прячутся, пока их не видно); вернулся на те же 150px, что и
     * при первом запуске (порог LazyLoad), — играет с начала.
     */
    const watchReplay = function (video) {
        if (!window.IntersectionObserver || video.loop || video.dataset.replayWatched) {
            return;
        }
        video.dataset.replayWatched = "1";

        let armed = false;

        const leave = new IntersectionObserver(function (entries) {
            if (entries[entries.length - 1].isIntersecting) {
                return;
            }
            const idx = pending.indexOf(video);

            if (idx !== -1) {
                pending.splice(idx, 1);
            }
            video.pause();

            if (video.readyState >= 1) {
                video.currentTime = 0;
            }
            setRevealed(video, false);
            armed = true;
        });

        const enter = new IntersectionObserver(function (entries) {
            if (!armed || !entries[entries.length - 1].isIntersecting) {
                return;
            }
            armed = false;
            start(video);
        }, { rootMargin: "-150px 0px" });

        leave.observe(video);
        enter.observe(video);
    };

    /**
     * @param {Element} root — контейнер с data-lazy-function либо сам <video>.
     */
    const play = function (root) {
        const video = root.tagName === "VIDEO" ? root : root.querySelector("[data-video-in-view]");

        if (!video || video.classList.contains(PLAYED_CLASS)) {
            return;
        }
        video.classList.add(PLAYED_CLASS);
        video.muted = true;
        video.playsInline = true;

        watchReveal(video);

        if (reducedMotion()) {
            DEBUG && console.log("videoInView: prefers-reduced-motion — конечный кадр без анимации");
            prime(video);
            showFinalState(video);
            return;
        }

        /* Обычно загрузка уже идёт — её начал watchApproach за экран до блока */
        prime(video);
        start(video);

        if (video.loop) {
            watchVisibility(video);
        } else {
            watchReplay(video);
        }
    };

    watchApproach();

    /* После подмены контента страницы (chooseProduct.js) — заново по новым роликам */
    module.refresh = watchApproach;

    module.callback = play;
    module.play = play;
});
